import Loader from '@/src/components/Loading/Loader'
import FrameSelector from '@/src/components/Player/GameFrame/FrameSelector'
import styles from '@/src/components/Player/GameFrame/GameFrame.module.scss'
import SGABar from '@/src/components/SGABar'
import SplashCard from '@/src/components/SplashCard/SplashCard'

import StatusScreen from '@/src/components/StatusScreen'
import { Heading } from '@/src/components/Typography'
import ServerError from '@/src/const/server-errors.tsx'
import useRouter from '@/src/hooks/Router/useRouter'
import useTabDetection from '@/src/hooks/useTabDetection'
import useTimeout from '@/src/hooks/useTimeout'
import { usePlayGameMutation } from '@/src/services/games'
import { gtmGameStart } from '@/src/services/gtm_factory.ts'
import { FamilyType } from '@/src/services/types/games'
import { gameByDevice } from '@/src/utils/helpers/thumbnails'
import { addBreadcrumb, captureMessage } from '@sentry/nextjs'
import cn from 'classnames'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

type DemoGameFrameProps = {
  gameFamily: FamilyType
  children?: React.ReactNode
  providername?: string | undefined
}

const DemoGameFrame = ({
  gameFamily,
  children,
  providername,
}: DemoGameFrameProps) => {
  const { t } = useTranslation(['game', 'compliance', 'common'])
  const router = useRouter()

  const { isLatestTab } = useTabDetection()
  const [iframeLoading, setIframeLoading] = useState(true)

  const [playGame, { data: gameData }] = usePlayGameMutation()
  const [game] = useState(gameByDevice(gameFamily.games))

  useEffect(() => {
    window.document.documentElement.classList.toggle(`app-game`, true)
    return () => {
      window.document.documentElement.classList.toggle(`app-game`, false)
    }
  }, [])

  useEffect(() => {
    if (!gameData) return

    // If api returns an error, show error screen
    // apart from game Disabled Exception
    if (
      gameData?.errorCode !== ServerError.GameDisabledException &&
      (gameData?.error || gameData?.errorCode)
    ) {
      addBreadcrumb({
        category: 'gamePlay',
        message: 'Demo Game launch url failed ',
        data: gameData,
        level: 'fatal',
      })
      captureMessage(`PlayUrl failed with: ${gameData.errorCode}`, 'fatal')
      // Make sure we reload the scripts on next game launch
      router.replace(`/error/${gameData?.errorCode || 100}`)
      return
    }

    // send GTM event
    gtmGameStart({ gameName: gameData?.name, logged_in: false })
  }, [gameData, gameFamily, router])

  // Fallback if iframe doesn't load
  useTimeout(() => {
    setIframeLoading(false)
  }, 10000)

  return (
    <>
      {iframeLoading && !!gameData && (
        <div className={cn(styles.loader)}>
          <div className='flex h-full w-full flex-col items-center justify-center text-center font-bold'>
            <Loader
              header={t('now-launching', {
                ns: 'game',
                name: gameFamily.familyName,
              })}
              type='inline'
            />
          </div>
        </div>
      )}
      <SGABar
        isLoggedIn={false}
        isInGame
        gameName={!iframeLoading ? gameFamily?.familyName : undefined}
        searchReplaceUrl
      />
      {!gameData ? (
        <>
          <SplashCard
            gameFamily={gameFamily}
            hideDemoUrl={game.hideDemoUrl}
            playDemoHandler={() =>
              playGame({
                mode: 'demo',
                name: game.name,
              })
            }
            providername={providername}
          />
          {children}
        </>
      ) : isLatestTab && !!gameData ? (
        <FrameSelector
          classNames={cn([styles.iframe])}
          gameFamily={gameFamily}
          gameUrl={gameData.url}
          setIframeLoading={setIframeLoading}
        />
      ) : (
        <StatusScreen classes='relative'>
          <Heading level={1}>{t('common:error')}</Heading>
          <p className='mb-8 px-8 text-center opacity-80'>
            {t('game:only-one-game-tab')}
          </p>
        </StatusScreen>
      )}
    </>
  )
}

export default DemoGameFrame
